function initProductCardSwipers(root = document) {
  let retryCount = 0;
  let productCardSwiperObserver;

  function initialize(swiperElement) {
    if (swiperElement.swiper || swiperElement.dataset.swiperInitialized === 'true') {
      return;
    }

    swiperElement.dataset.swiperInitialized = 'true';

    const allowTouchMove = swiperElement.closest('.product-list:not(.product-list--carousel)') !== null;

    const swiper = new Swiper(swiperElement, {
      effect: 'slide',
      speed: 0,
      autoHeight: false,
      touchRatio: 1,
      touchAngle: 45,
      allowTouchMove: allowTouchMove,
      watchSlidesProgress: true,
      preventInteractionOnTransition: false,
      pagination: {
        el: ".swiper-pagination",
        type: "progressbar",
      },
      navigation: {
        nextEl: ".card-arrow--next",
        prevEl: ".card-arrow--prev",
      },
      breakpoints: {
        320: {
          slidesPerView: 1,
          spaceBetween: 0
        }
      }
    });

    setupProductCardSliderControls(swiperElement, swiper);
  }

  function observeProductCardSwipers() {
    const productCardSwipers = root.querySelectorAll('.product-card__swiper:not([data-swiper-initialized="true"])');

    if (productCardSwipers.length === 0) {
      return;
    }

    productCardSwiperObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          initialize(entry.target);
          productCardSwiperObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: "200px" });

    productCardSwipers.forEach(el => {
      const rect = el.getBoundingClientRect();
      const isNearViewport = rect.width > 0 && rect.height > 0 && rect.bottom >= -200 && rect.top <= window.innerHeight + 200;

      if (isNearViewport) {
        initialize(el);
      } else {
        productCardSwiperObserver.observe(el);
      }
    });
  }

  function runWhenSwiperIsReady() {
    if (window.Swiper) {
      observeProductCardSwipers();
      return;
    }

    if (retryCount < 40) {
      retryCount += 1;
      window.setTimeout(runWhenSwiperIsReady, 100);
    }
  }

  runWhenSwiperIsReady();
}

function setupProductCardSliderControls(swiperElement, swiper) {
  const wrapper = swiperElement.querySelector('.swiper-wrapper');
  const slides = Array.from(swiperElement.querySelectorAll('.swiper-slide'));
  const progressFill = swiperElement.querySelector('.swiper-pagination-progressbar-fill');
  const previousButton = swiperElement.querySelector('.card-arrow--prev');
  const nextButton = swiperElement.querySelector('.card-arrow--next');

  if (!wrapper || slides.length <= 1) {
    return;
  }

  let activeIndex = 0;
  let startX = 0;
  let startY = 0;
  let isDragging = false;
  let didSwipe = false;

  function setActiveSlide(index) {
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    const slideWidth = swiperElement.clientWidth || swiper.width || swiperElement.getBoundingClientRect().width;
    const previousIndex = activeIndex;

    activeIndex = nextIndex;
    wrapper.style.transitionDuration = '0ms';
    wrapper.style.transform = `translate3d(${-slideWidth * activeIndex}px, 0px, 0px)`;

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('swiper-slide-active', slideIndex === activeIndex);
      slide.classList.toggle('swiper-slide-prev', slideIndex === activeIndex - 1);
      slide.classList.toggle('swiper-slide-next', slideIndex === activeIndex + 1);
    });

    if (progressFill) {
      progressFill.style.transform = `translate3d(0px, 0px, 0px) scaleX(${(activeIndex + 1) / slides.length}) scaleY(1)`;
      progressFill.style.transitionDuration = '0ms';
    }

    if (previousButton) {
      previousButton.classList.toggle('swiper-button-disabled', activeIndex === 0);
      previousButton.setAttribute('aria-disabled', activeIndex === 0 ? 'true' : 'false');
    }

    if (nextButton) {
      nextButton.classList.toggle('swiper-button-disabled', activeIndex === slides.length - 1);
      nextButton.setAttribute('aria-disabled', activeIndex === slides.length - 1 ? 'true' : 'false');
    }

    swiper.previousIndex = previousIndex;
    swiper.activeIndex = activeIndex;
    swiper.realIndex = activeIndex;
    swiper.snapIndex = activeIndex;
    swiper.translate = -slideWidth * activeIndex;
    swiper.isBeginning = activeIndex === 0;
    swiper.isEnd = activeIndex === slides.length - 1;
    swiper.animating = false;
  }

  function onTouchStart(event) {
    const touch = event.touches[0];

    startX = touch.clientX;
    startY = touch.clientY;
    isDragging = true;
    didSwipe = false;
  }

  function onTouchMove(event) {
    if (!isDragging) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
      didSwipe = true;
      event.preventDefault();
    }
  }

  function onTouchEnd(event) {
    if (!isDragging) {
      return;
    }

    isDragging = false;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    if (Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      setActiveSlide(activeIndex + (deltaX < 0 ? 1 : -1));
    }
  }

  swiperElement.addEventListener('touchstart', onTouchStart, { passive: true });
  swiperElement.addEventListener('touchmove', onTouchMove, { passive: false });
  swiperElement.addEventListener('touchend', onTouchEnd, { passive: false });

  swiperElement.addEventListener('click', function(event) {
    if (didSwipe) {
      event.preventDefault();
      event.stopPropagation();
      didSwipe = false;
    }
  }, true);

  previousButton?.addEventListener('click', function(event) {
    event.preventDefault();
    event.stopPropagation();
    setActiveSlide(activeIndex - 1);
  });

  nextButton?.addEventListener('click', function(event) {
    event.preventDefault();
    event.stopPropagation();
    setActiveSlide(activeIndex + 1);
  });

  window.addEventListener('resize', function() {
    setActiveSlide(activeIndex);
  });

  setActiveSlide(0);
}

function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

onDocumentReady(function() {
  initProductCardSwipers();

  document.addEventListener('facet:update', function() {
    window.setTimeout(function() {
      initProductCardSwipers();
    }, 0);
  });

  document.addEventListener('shopify:section:load', function(event) {
    initProductCardSwipers(event.target);
  });

  // Desktop scroll animation for product-info__block-group
  function initDesktopScrollAnimation() {
    // Only run on desktop (you can adjust the breakpoint as needed)
    if (window.innerWidth <= 768) return;
    
    const blockGroups = document.querySelectorAll('.product-info__block-group');
    if (blockGroups.length === 0) return;
    
    let isAnimating = false;
    let originalMaxHeights = [];
    let currentState = 'expanded'; // Track current state: 'expanded' or 'collapsed'
    
    // Store original max-height values
    blockGroups.forEach((group, index) => {
      const computedStyle = window.getComputedStyle(group);
      const maxHeight = computedStyle.maxHeight;
      // Convert 'none' to actual pixel value or use a fallback
      if (maxHeight === 'none') {
        // Get the actual height as fallback
        originalMaxHeights[index] = group.offsetHeight + 'px';
      } else {
        originalMaxHeights[index] = maxHeight;
      }
    });
    
    function handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeCollapsed = scrollTop > 300;
      
      // Only animate if state needs to change
      if ((shouldBeCollapsed && currentState === 'expanded') || 
          (!shouldBeCollapsed && currentState === 'collapsed')) {
        
        if (isAnimating) {
          // If already animating, force complete the current animation
          blockGroups.forEach((group, index) => {
            group.style.transition = 'none';
            if (currentState === 'expanded') {
              group.style.maxHeight = '0';
            } else {
              group.style.maxHeight = originalMaxHeights[index];
            }
          });
          
          // Force reflow
          blockGroups[0].offsetHeight;
        }
        
        isAnimating = true;
        currentState = shouldBeCollapsed ? 'collapsed' : 'expanded';
        
        blockGroups.forEach((group, index) => {
          if (shouldBeCollapsed) {
            // Animate to max-height: 0
            group.style.transition = 'max-height 0.3s ease';
            group.style.maxHeight = '0';
            const parent = group.parentElement;
            if (parent) {
              parent.classList.add('on--scroll');
            }
          } else {
            // Restore original max-height
            group.style.transition = 'max-height 0.3s ease';
            group.style.maxHeight = originalMaxHeights[index];
            setTimeout(() => {
              group.style.maxHeight = 'unset';
            }, 500);
            const parent = group.parentElement;
            if (parent) {
              parent.classList.remove('on--scroll');
            }
          }
        });
        
        // Reset animation flag after transition
        setTimeout(() => {
          isAnimating = false;
        }, 300);
      }
    }
    
    // Throttle scroll events for better performance
    let ticking = false;
    function throttledScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }
    
    // Add scroll event listener
    window.addEventListener('scroll', throttledScroll);
    
    // Handle window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth <= 768) {
        // Reset to original values on mobile
        blockGroups.forEach((group, index) => {
          group.style.transition = '';
          group.style.maxHeight = originalMaxHeights[index];
          setTimeout(() => {
            group.style.maxHeight = 'unset';
          }, 300);
          const parent = group.parentElement;
          if (parent) {
            parent.classList.remove('on--scroll');
          }
        });
      }
    });
    
    // Initial call to set correct state on page load
    handleScroll();
  }
  
  // Initialize the scroll animation
  // initDesktopScrollAnimation();
});

document.querySelectorAll('.product-info__block-item').forEach(block => {
    const content = block.querySelector('.accordion__content');
    if (!content) return;

    content.querySelectorAll('p').forEach(p => {
        const text = p.textContent.trim();

        if (text === '' || /^[^:]+:\s*$/.test(text)) {
            p.remove();
        }
    });

    if (content.textContent.trim() === '' && content.children.length === 0) {
        block.remove();
    }
});
// Wishlist Page: Ensure Add to Cart opens the cart drawer instead of cart page
document.addEventListener('submit', function(event) {
    const form = event.target;
    if (form && form.classList && form.classList.contains('wk-form')) {
        setTimeout(function() {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            const cartDrawer = document.getElementById('cart-drawer');
            if (cartDrawer && typeof cartDrawer.show === 'function') {
                cartDrawer.show();
            }
        }, 500);
    }
}, true);
